import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, Users, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_HERO_IMAGE = "/img/canyon-lg.webp";

const categoryLabels = {
  'haloterapia': 'Haloterapia',
  'especiales': 'Sesiones Especiales',
  'familias': 'Familias',
  'masajes': 'Masajes'
};

export const ServiciosPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [content, setContent] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, contentRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/content`)
        ]);
        setServices(servicesRes.data);
        
        // Parse content for servicios page
        const pageContent = {};
        contentRes.data.forEach(item => {
          if (item.page === 'servicios') {
            pageContent[item.section] = item;
          }
        });
        setContent(pageContent);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const categories = ['all', ...new Set(services.map(s => s.category))];
  
  const filteredServices = filter === 'all' 
    ? services 
    : services.filter(s => s.category === filter);

  const heroContent = content.hero || {};
  const heroImage = heroContent.image_url || DEFAULT_HERO_IMAGE;

  return (
    <div className="bg-halo-cream min-h-screen" data-testid="servicios-page">
      {/* Hero */}
      <section className="relative py-24 bg-halo-charcoal overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={heroImage} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-halo-brown font-medium tracking-widest text-sm">
              {heroContent.subtitle || '🌿 TARIFAS HALO'}
            </span>
            <h1 className="font-heading text-5xl md:text-6xl text-halo-cream mt-4 mb-6">
              {heroContent.title || 'Servicios Individuales'}
            </h1>
            <p className="text-halo-cream/80 text-lg leading-relaxed">
              {heroContent.text || 'En HALO cada sesión está diseñada bajo principios de neurodiseño, en un entorno de calma, regulación sensorial y ambiente de sal seca micronizada.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Premium Hours Info */}
      <section className="py-8 bg-halo-brown/10 border-b border-halo-taupe/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-halo-charcoal">
            <span className="font-medium">⏰ Horario Premium:</span>
            <span>L-J: 18:00-20:30</span>
            <span className="text-halo-taupe">|</span>
            <span>Viernes: desde 17:00</span>
            <span className="text-halo-taupe">|</span>
            <span>Sábado: 10:00-13:00 y 17:00-19:00</span>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-halo-surface sticky top-[80px] z-40 border-b border-halo-taupe/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  filter === cat
                    ? 'bg-halo-brown text-halo-cream'
                    : 'bg-halo-cream text-halo-charcoal hover:bg-halo-brown/20 border border-halo-taupe/20'
                }`}
                data-testid={`filter-${cat}`}
              >
                {cat === 'all' ? 'Todos' : categoryLabels[cat] || cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-[450px] skeleton rounded-2xl" />
              ))}
            </div>
          ) : (
            <motion.div layout className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredServices.map((service, index) => (
                <motion.div
                  key={service.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-halo-surface rounded-2xl overflow-hidden border border-halo-taupe/10 hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image_url}
                      alt={service.name}
                      className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-halo-brown/90 text-halo-cream text-xs px-3 py-1 rounded-full">
                        {categoryLabels[service.category] || service.category}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="font-heading text-xl text-halo-charcoal mb-2">
                      {service.name}
                    </h3>
                    <p className="text-halo-text text-sm mb-4 line-clamp-2">
                      {service.description}
                    </p>
                    
                    <div className="flex items-center gap-4 mb-4 text-sm text-halo-text">
                      <span className="flex items-center gap-1">
                        <Clock size={14} /> {service.duration} min
                      </span>
                    </div>

                    {/* Price display */}
                    <div className="mb-4 p-3 bg-halo-cream rounded-lg">
                      {service.price_premium ? (
                        <div className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-halo-text">Entre semana</span>
                            <span className="font-heading text-lg text-halo-brown">{service.price}€</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-halo-text">Premium/Fin de semana</span>
                            <span className="font-heading text-lg text-halo-charcoal">{service.price_premium}€</span>
                          </div>
                        </div>
                      ) : service.price_note ? (
                        <div className="text-center">
                          <span className="font-heading text-2xl text-halo-brown">{service.price}€</span>
                          <p className="text-xs text-halo-text mt-1">{service.price_note}</p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <span className="font-heading text-2xl text-halo-brown">{service.price}€</span>
                        </div>
                      )}
                    </div>

                    {/* Benefits */}
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1">
                        {service.benefits?.slice(0, 3).map((benefit, i) => (
                          <span key={i} className="text-xs bg-halo-sage/20 text-halo-charcoal px-2 py-1 rounded-full">
                            {benefit}
                          </span>
                        ))}
                      </div>
                    </div>

                    <Link to="/bonos">
                      <Button className="w-full bg-halo-brown hover:bg-halo-charcoal text-halo-cream">
                        Ver bonos
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-halo-surface">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <Sparkles className="w-12 h-12 text-halo-brown mx-auto" />
            <h2 className="font-heading text-3xl text-halo-charcoal">
              ¿Buscas continuidad?
            </h2>
            <p className="text-halo-text leading-relaxed">
              Descubre nuestras membresías y crea un hábito de bienestar sostenido 
              con importantes ventajas y ahorro.
            </p>
            <Link to="/membresias">
              <Button size="lg" className="bg-halo-brown hover:bg-halo-charcoal">
                Ver Membresías
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServiciosPage;
